// Geometry for one row of the weekly rotation grid.
//
// The data model stores events per week with a Monday-anchored day and an
// optional week offset. The grid draws Sunday first, so displayed row N covers
// [Sunday of data-week N-1 .. Saturday of data-week N]. That offset is why this
// is not a straight read of `weeks[n].events`: a Friday-to-Monday block belongs
// to week N's events[] but paints across two rows, and row 0's leading Sunday
// comes from the LAST week of the cycle.
//
// Everything here is a pure function of the rotation. Display strings that need
// party names or time formatting are supplied by the caller through `headLabel`.

import { DAY_INDEX, MIN_PER_DAY, MIN_PER_WEEK, timeToMin } from './weekMath.js'
import { continuousRuns } from './runs.js'

/**
 * Events whose blocks BEGIN in the displayed row, in chronological order.
 * A block starting on a Sunday is drawn at the head of the row AFTER its data
 * week, so its summary sentence lists there too.
 *
 * Blocks that merely continue the same parent's previous block are left out:
 * that seam is not a handover, so the run gets one sentence, carried on the
 * `run` property for the caller to read the real end and transition from.
 */
export function collectSummaryEvents({ allWeeks, weekIndex, totalWeeks }) {
  const N = totalWeeks
  const rowStart = weekIndex * MIN_PER_WEEK - MIN_PER_DAY
  const cycle = N * MIN_PER_WEEK
  const { byKey } = continuousRuns(allWeeks || [])
  const list = []
  ;(allWeeks || []).forEach((week, wi) => {
    ;(week?.events || []).forEach((e, ei) => {
      const startRow = (wi + (e.startWeekOffset || 0) + (e.startDay === 'sun' ? 1 : 0)) % N
      if (startRow !== weekIndex) return
      const info = byKey.get(`${wi}:${ei}`)
      if (info && info.isContinuation) return // headed by an earlier block
      const flat = (wi + (e.startWeekOffset || 0)) * MIN_PER_WEEK
        + (DAY_INDEX[e.startDay] ?? 0) * MIN_PER_DAY + timeToMin(e.startTime)
      list.push({ e, wi, ei, run: info ? info.run : null, order: ((flat - rowStart) % cycle + cycle) % cycle })
    })
  })
  return list.sort((a, b) => a.order - b.order)
}

/**
 * Project every event in the rotation onto a flattened cycle timeline (minutes
 * from Week 1 Monday 00:00), then slice out this row's window and cut it into
 * one segment per calendar day.
 */
function collectSegments({ allWeeks, weekIndex, totalWeeks, headLabel }) {
  const cycleMins = totalWeeks * MIN_PER_WEEK
  const { byKey } = continuousRuns(allWeeks || [])
  const winStart = weekIndex * MIN_PER_WEEK - MIN_PER_DAY // Sunday of the previous data week
  const winEnd = winStart + MIN_PER_WEEK                  // through this week's Saturday 24:00
  const all = []

  ;(allWeeks || []).forEach((week, wi) => {
    ;(week?.events || []).forEach((e, ei) => {
      const sOff = e.startWeekOffset || 0
      const eOff = e.endWeekOffset || 0
      let start = (wi + sOff) * MIN_PER_WEEK + (DAY_INDEX[e.startDay] ?? 0) * MIN_PER_DAY + timeToMin(e.startTime)
      let end = (wi + eOff) * MIN_PER_WEEK + (DAY_INDEX[e.endDay] ?? 0) * MIN_PER_DAY + timeToMin(e.endTime)
      if (end <= start) end += MIN_PER_WEEK // safety, matches rotation.js
      // Normalize start into [0, cycleMins)
      const shift = Math.floor(start / cycleMins) * cycleMins
      start -= shift
      end -= shift

      // An event near the cycle end can spill into row 1's leading Sunday,
      // so also test the copy shifted one cycle back.
      for (const k of [-1, 0]) {
        const a = Math.max(start + k * cycleMins, winStart)
        const b = Math.min(end + k * cycleMins, winEnd)
        if (b <= a) continue
        // Split [a, b) into per-day segments within this row's window.
        let t = a
        while (t < b) {
          const dayCol = Math.floor((t - winStart) / MIN_PER_DAY) // 0=Sun … 6=Sat
          const dayStart = winStart + dayCol * MIN_PER_DAY
          const segEnd = Math.min(b, dayStart + MIN_PER_DAY)
          const isFirst = t === start + k * cycleMins
          const startRow = Math.floor((t - dayStart) / 60)
          const rowSpan = Math.max(startRow + 1, Math.ceil((segEnd - dayStart) / 60)) - startRow
          all.push({
            key: `${wi}:${ei}:${k}:${dayCol}`,
            dayCol,
            startMin: t - dayStart,
            endMin: segEnd - dayStart,
            partner: e.partner,
            isFirst,
            isLast: segEnd === end + k * cycleMins,
            eventIndex: ei,
            sourceWeekIndex: wi, // which week's events[] this event lives in
            title: `${e.partner === 'A' ? 'Party A' : 'Party B'}: ${e.startDay} ${e.startTime} to ${e.endDay} ${e.endTime}`,
            headLabel: isFirst ? headLabel(e, rowSpan, byKey.get(`${wi}:${ei}`)) : '',
          })
          t = segEnd
        }
      }
    })
  })

  return all
}

/**
 * Assign each segment a lane within its day so that overlapping blocks render
 * side by side instead of on top of one another. Mutates and returns `all`.
 *
 * Greedy first-fit: segments are taken earliest-first and each one drops into
 * the lowest lane whose current end time does not run past its start.
 */
function assignLanes(all) {
  const byDay = {}
  for (const s of all) {
    if (!byDay[s.dayCol]) byDay[s.dayCol] = []
    byDay[s.dayCol].push(s)
  }
  for (const day in byDay) {
    const daySegs = byDay[day].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin)
    const laneEnds = []
    for (const seg of daySegs) {
      let laneIdx = laneEnds.findIndex((e) => e <= seg.startMin)
      if (laneIdx === -1) { laneIdx = laneEnds.length; laneEnds.push(seg.endMin) }
      else laneEnds[laneIdx] = seg.endMin
      seg.lane = laneIdx
    }
    const laneCount = laneEnds.length
    for (const seg of daySegs) seg.laneCount = laneCount
  }
  return all
}

/**
 * Every drawable segment for one displayed row, lane-packed.
 *
 * @param headLabel (event, rowSpan, runInfo) => string — the caption for a
 *        block's first segment. Needs party names and a time format, so the
 *        caller supplies it; runInfo says whether the block heads a run or
 *        continues one.
 */
export function buildSegments({ allWeeks, weekIndex, totalWeeks, headLabel = () => '' }) {
  return assignLanes(collectSegments({ allWeeks, weekIndex, totalWeeks, headLabel }))
}
